import { nameIn, t, type StaffLanguage } from './staffText'
import type { QueueView, Service } from './types'

/**
 * A visit that needs more than one thing.
 *
 * Every rule the two screens turn on is here rather than inside them, because the
 * web suite runs in Node with no DOM and nothing can render a component in a test.
 * A rule left in a component is a rule nothing executes until a branch does.
 *
 * All of these mirror something in the Go and none of them decide anything: the
 * server checks the cap, the routing and the repeats again, and refuses in its own
 * words. What these are for is saying the same thing a moment earlier, at the desk,
 * where the operator can still act on it — a refusal that arrives after the press
 * is a refusal the customer watched happen.
 */

/** The most services one visit may carry, the one in progress included.
 *
 *  Mirrors queue.MaxPlannedServices, which is deliberately a constant of the
 *  product rather than a branch setting: a limit that can be raised is a limit
 *  nobody argues with. Duplicated here rather than fetched for the reason
 *  NOTE_LIMIT is — the server refuses a seventh whatever this says, so the worst a
 *  drift can do is show the wrong number in a sentence. */
export const MAX_PLANNED_SERVICES = 6

/** What the button that ends this piece of work should say.
 *
 *  "Finish" is a promise about the visit, and on a ticket that still owes the
 *  customer something it is not true: the close consumes the head of the plan and
 *  the visit carries on. An operator who reads Finish, presses it, and watches the
 *  customer stay where they are has been told something false by the screen, and
 *  the next thing they stop trusting is the rest of the deck. */
export function closeLabel(planned: readonly string[] | undefined, lang: StaffLanguage): string {
  return t(planned && planned.length > 0 ? 'floor.nextService' : 'floor.finish', lang)
}

/** Why one more service may not go on this visit, in a sentence, or the empty
 *  string where it may.
 *
 *  The three checks are engine.resolveAlsoServices's, asked in its order, which
 *  matters more than it looks: the server refuses a full visit on the count alone
 *  before it has looked at any service, so a page that answered "already on this
 *  visit" where the server answers "too many" would send the operator to fix the
 *  wrong thing.
 *
 *  @param serviceId the service being asked for
 *  @param committed how many services the visit already carries — the one in
 *         progress plus everything planned at a desk, and the arrival about to be
 *         chosen plus everything staged at reception
 *  @param already the services the visit has, which none of it may repeat
 *  @param services what this branch offers, which is only ever the services that
 *         route somewhere in it */
export function whyNotPlanned(
  serviceId: string,
  committed: number,
  already: readonly string[],
  services: readonly Service[],
  lang: StaffLanguage,
): string {
  if (committed >= MAX_PLANNED_SERVICES) {
    return t('floor.planMax', lang, { n: MAX_PLANNED_SERVICES })
  }
  if (!services.some((s) => s.id === serviceId)) {
    return t('floor.notOfferedHere', lang)
  }
  if (already.includes(serviceId)) {
    return t('floor.alreadyOnVisit', lang, { service: serviceLabel(services, serviceId, lang) })
  }
  return ''
}

/** The queue a service routes into, or nothing where it routes nowhere.
 *
 *  The queues are the branch's live ones, off the snapshot the screen is already
 *  streaming, and never GET /branches/{id}/queues. That endpoint answers from the
 *  draft and belongs to the administration screens: a floor screen reading the
 *  draft is the main risk docs/BRANCH-CONFIG.md names, and a counter that predicted
 *  from it would be one publish ahead of the branch it is standing in. The snapshot
 *  is the floor by construction, so the answer is taken from there and nowhere
 *  else. Anything calling this with a list fetched from that endpoint has
 *  reintroduced the thing this sentence exists to prevent.
 *
 *  What is left is catalog.NewRouting's tie-break, kept the same on purpose: a
 *  branch that has put one service in two queues gets the lowest sort order and
 *  then the lowest code. Not because that is the right answer — it is a
 *  misconfiguration either way — but because it is the answer the server will give,
 *  and a screen that guessed differently would promise a desk work that goes to
 *  another line. There is no retired queue to skip here: engine.queueViews leaves
 *  an inactive queue off the snapshot altogether, so one never arrives. */
export function queueForService(
  queues: readonly QueueView[],
  serviceId: string,
): QueueView | undefined {
  let found: QueueView | undefined
  for (const q of queues) {
    if (!q.serviceIds?.includes(serviceId)) continue
    if (!found || q.sortOrder < found.sortOrder || (q.sortOrder === found.sortOrder && q.code < found.code)) {
      found = q
    }
  }
  return found
}

/** Where the customer will be when the next service begins. */
export type NextStop =
  | { at: 'desk' }
  | { at: 'queue'; queueId: string }
  | { at: 'nowhere' }

/** Whether the next service happens at this desk, in another line, or not at all.
 *
 *  The same question engine.advancePlan asks at the close, asked here so the
 *  operator can read the answer before they press the button rather than after.
 *  "Nowhere" is the case a screen would never think of and the branch will meet
 *  eventually: a service planned when it routed and retired from every queue while
 *  the customer sat at the desk. The engine ends the visit and logs the plan as
 *  abandoned, and the operator should know that is what the button is about to do.
 *
 *  @param queues the branch's live queues, off the snapshot — see queueForService
 *  @param deskQueueIds the queues this desk's own work profile serves */
export function nextStop(
  serviceId: string,
  queues: readonly QueueView[],
  deskQueueIds: readonly string[],
): NextStop {
  const target = queueForService(queues, serviceId)
  if (!target) return { at: 'nowhere' }
  if (deskQueueIds.includes(target.queueId)) return { at: 'desk' }
  return { at: 'queue', queueId: target.queueId }
}

/** What the desk is told the close is about to do.
 *
 *  Three sentences rather than one with a clause in it, because the three outcomes
 *  are not degrees of the same thing. The queue one says the wait is kept, which is
 *  the fact an operator needs in order not to apologise for something the product
 *  did not do: a customer sent on by a plan does not go to the back of the line. */
export function nextServiceSentence(
  displayNumber: string,
  serviceName: string,
  stop: NextStop,
  lang: StaffLanguage,
): string {
  const vars = { number: displayNumber, service: serviceName }
  switch (stop.at) {
    case 'desk':
      return t('floor.stop.desk', lang, vars)
    case 'queue':
      return t('floor.stop.queue', lang, vars)
    default:
      return t('floor.stop.none', lang, vars)
  }
}

/** A service's name for a sentence, falling back to its code.
 *
 *  Named rather than coded wherever a person reads it: the operator is being told
 *  where somebody standing in front of them is about to go, and "LOANS" is not
 *  where anybody goes. A service the catalogue on this screen has never heard of —
 *  a plan made before a publish retired it — still has to read as a sentence
 *  rather than as a gap. */
export function serviceLabel(services: readonly Service[], serviceId: string, lang: StaffLanguage): string {
  const s = services.find((x) => x.id === serviceId)
  if (!s) return t('floor.theNextService', lang)
  return nameIn(s.name, lang, s.code)
}
